import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogMain,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ncoder/ui';
import { grantableRolesFor } from './roleOptions';
import type { CreateOrgInvitationInput, ManagementLevel, OrgRole } from '@/services/org-admin';

// Anchor below the trigger (Radix's default "item-aligned" mode opens in an
// unpredictable direction/position) and scroll instead of overflowing once
// the list is taller than the viewport allows.
const SELECT_CONTENT_PROPS = {
  position: 'popper' as const,
  sideOffset: 4,
  className: 'max-h-60 w-[--radix-select-trigger-width] overflow-y-auto',
};

interface InviteFormValues {
  email: string;
  role_id: string;
}

export interface InviteMemberModalProps {
  open: boolean;
  roles: OrgRole[];
  currentUserRole: ManagementLevel | null;
  isSubmitting: boolean;
  onSubmit: (data: CreateOrgInvitationInput) => void;
  onClose: () => void;
}

export function InviteMemberModal({
  open,
  roles,
  currentUserRole,
  isSubmitting,
  onSubmit,
  onClose,
}: InviteMemberModalProps) {
  const roleOptions = grantableRolesFor(currentUserRole, roles);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<InviteFormValues>({
    defaultValues: { email: '', role_id: roleOptions[0]?.id ?? '' },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!open) return;
    reset({ email: '', role_id: roleOptions[0]?.id ?? '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset]);

  const handleCancel = () => onClose();

  const submit = handleSubmit((data) => {
    onSubmit({ email: data.email, role_id: data.role_id });
  });

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">Invite member</DialogTitle>
          <DialogDescription className="text-xs">
            Invite someone by email and assign the role they'll join with.
          </DialogDescription>
        </DialogHeader>

        <DialogMain className="space-y-3">
          <div className="space-y-1.5">
            <label htmlFor="invite-email" className="text-xs font-medium text-foreground">
              Email
            </label>
            <input
              id="invite-email"
              type="email"
              placeholder="name@company.com"
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /\S+@\S+\.\S+/, message: 'Enter a valid email' },
              })}
              disabled={isSubmitting}
              className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            />
            {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">
              Role <span className="text-destructive">*</span>
            </label>
            <Controller
              name="role_id"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange} disabled={isSubmitting}>
                  <SelectTrigger className="text-xs">
                    <SelectValue placeholder="Select a role…" />
                  </SelectTrigger>
                  <SelectContent {...SELECT_CONTENT_PROPS}>
                    {roleOptions.map((role) => (
                      <SelectItem key={role.id} value={role.id} className="text-xs">
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </DialogMain>

        <DialogFooter>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSubmitting}
            className="rounded-lg border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!isValid || roleOptions.length === 0 || isSubmitting}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs text-primary-foreground transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
          >
            {isSubmitting && <Loader2 className="h-3 w-3 animate-spin" />}
            {isSubmitting ? 'Sending…' : 'Send invite'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
